/**
 * "Skip to content" — the first focusable element in the document, and the
 * only way a keyboard user reaches the page without paying for the whole
 * rail first (flowstack#983).
 *
 * The target already existed: `AppShell` has rendered `<main id="main">`
 * since the shell was written. Nothing linked to it, so every page cost
 * ~13 tab stops — the rail's brand zone, its 7 destinations and its account
 * group — before the first stop inside the page. That was equally true
 * before #953 reordered the chrome; the reorder only made it visible.
 *
 * Three details are load-bearing, and each one produces a skip link that
 * LOOKS right and does nothing:
 *
 * 1. **Hidden must not mean unfocusable.** `display: none` and
 *    `visibility: hidden` take an element out of the tab order entirely,
 *    which is the original defect with extra code. This is `position:
 *    fixed` + a transform that parks it above the viewport: it is always
 *    rendered, always focusable, always announced in DOM order, and
 *    `focus:translate-y-0` brings it back. A transform is preferred over
 *    the `sr-only focus:not-sr-only` pair because that pair depends on
 *    Tailwind emitting the `position` plugin after the accessibility one —
 *    true today, and an invisible coupling to plugin order. One property
 *    moves here, so there is nothing to order.
 *
 *    The move is deliberately NOT transitioned. A skip link is reached by
 *    keyboard and acted on immediately; 150ms of travel buys nothing and
 *    means the link is still off-screen at the moment it takes focus.
 *    `app_frame.spec.ts` measures its box the instant it is focused, so a
 *    transition reintroduced here would fail there rather than pass
 *    quietly.
 *
 *    Being `fixed` also keeps it out of the shell's flex row in BOTH
 *    states: a static skip link would become a row item on focus and shove
 *    the rail sideways. And a fixed box parked above the viewport cannot
 *    push the document into a scrollbar, which `app_frame.spec.ts` asserts
 *    against.
 *
 * 2. **It has to be genuinely first.** Not first in the rail, first in the
 *    document — anything ahead of it is a stop the user still pays. Hence
 *    it is `AppShell`'s first child, above `<Sidebar />`.
 *
 * 3. **Focus must actually move.** An in-page anchor jump moves the SCROLL
 *    position; whether it moves focus varies by browser, and where it does
 *    not, the next Tab resumes from the rail and the link accomplishes
 *    nothing a screen reader can tell. So the click is handled explicitly:
 *    `<main>` carries `tabIndex={-1}` (programmatically focusable, still
 *    not a tab stop of its own) and we focus it ourselves. `preventDefault`
 *    then keeps `#main` out of the URL — the href stays because it is what
 *    makes this a link rather than a button-shaped div, and what assistive
 *    tech announces.
 *
 * It renders over the rail, so its surface is the `--sidebar*` family
 * rather than `--background`, and `--ring` is re-pointed at
 * `--sidebar-ring` the same way `.app-rail` does it — the accent ring
 * draws at 2.85:1 on the light tint, under the 3:1 bar for a non-text
 * indicator. `tests/e2e/app_frame.spec.ts` measures both themes.
 */
export function SkipToContent(): JSX.Element {
  return (
    <a
      href="#main"
      onClick={(event) => {
        const main = document.getElementById("main");
        if (!main) return; // Let the anchor jump have its way rather than swallowing the click.
        event.preventDefault();
        main.focus();
      }}
      className="fixed left-3 top-3 z-50 -translate-y-[200%] rounded-md border border-sidebar-border bg-sidebar px-4 py-2 text-sm font-medium text-sidebar-foreground shadow-lg [--ring:var(--sidebar-ring)] focus:translate-y-0"
    >
      Skip to content
    </a>
  );
}
